import { Button, Grid2, InputLabel, TextField } from '@mui/material';
import { BookingOrigin, CommissionType } from '@utriper/core';
import { SelectComponent, Title } from '@utriper/core-ui';
import { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

const CommissionForm = ({ commission, onSubmit = () => {}, onClose }) => {
    const { t: translate, i18n } = useTranslation();
    const i18nLang = i18n.language;
    const [isChanged, setIsChanged] = useState(false);

    const excludeBookingOriginIds = new Set([BookingOrigin.IDS.undefined, BookingOrigin.IDS.webapp, BookingOrigin.IDS.mobileapp, BookingOrigin.IDS.code]);
    const bookingOriginOptions = useMemo(() => {
        const ids = BookingOrigin.getIds();

        const filteredIds = ids.filter((id) => !excludeBookingOriginIds.has(id));

        return filteredIds.map((id) => {
            const translated = BookingOrigin.getTranslatedName(BookingOrigin.types, id, i18nLang);
            return { name: translated?.[0] ?? '', id };
        });
    }, [i18nLang]);

    const feeTypeOptions = useMemo(
        () => [
            ...CommissionType?.getIds().map((id) => {
                const translated = CommissionType.getTranslatedName(CommissionType.types, id, i18n.language);
                return { name: translated?.[0] ?? '', id };
            }),
        ],
        [i18n.language]
    );

    const currencyOptions = [
        { name: '', id: '' },
        {
            id: 'COP',
            name: 'COP',
        },
        {
            id: 'USD',
            name: 'USD',
        },
    ];

    return (
        <Grid2 py={2}>
            <Form
                onSubmit={onSubmit}
                initialValues={commission}
                render={({ handleSubmit, form, values }) => {
                    const { feeType = 0 } = values || {};
                    return (
                        <Grid2 container flexDirection="column" spacing={1}>
                            <Grid2>
                                <Title value={translate('experiencer.commissions')} size="l" />
                            </Grid2>
                            <Grid2 container flexDirection="column" spacing={3}>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('experiencer.commissionForm.bookingOrigin')}</InputLabel>
                                    <Field id="bookingOriginId" name="bookingOriginId">
                                        {({ input }) => (
                                            <SelectComponent
                                                label={translate('experiencer.commissionForm.bookingOrigin')}
                                                component={SelectComponent}
                                                values={bookingOriginOptions}
                                                style={{ width: '100%' }}
                                                value={input.value}
                                                onFieldChange={(e) => {
                                                    let value = e.target.value;

                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('experiencer.commissionForm.feeType')}</InputLabel>
                                    <Field id="feeType" name="feeType">
                                        {({ input }) => (
                                            <SelectComponent
                                                label={translate('experiencer.commissionForm.feeType')}
                                                component={SelectComponent}
                                                values={feeTypeOptions}
                                                style={{ width: '100%' }}
                                                value={input.value}
                                                onFieldChange={(e) => {
                                                    let value = e.target.value;

                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('experiencer.commissionForm.feeRate')}</InputLabel>
                                    <Field id="feeRate" name="feeRate">
                                        {({ input }) => (
                                            <TextField
                                                {...input}
                                                type="number"
                                                variant="outlined"
                                                hintText={'feeRate'}
                                                label={translate('experiencer.commissionForm.feeRate')}
                                                sx={{ width: '100%' }}
                                                onChange={(e) => {
                                                    let value = parseFloat(e.target.value) || 0;

                                                    if (feeType === 0) {
                                                        if (value > 100) value = 100;
                                                        else if (value < 0) value = 0;
                                                    } else {
                                                        if (value < 0) value = 0;
                                                    }

                                                    value = Math.round(value * 10000) / 10000; // limitar a 4 decimalnes
                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 width="100%">
                                    <InputLabel sx={{ marginBottom: '.5em' }}>{translate('experiencer.commissionForm.currency')}</InputLabel>
                                    <Field id="currency" name="currency">
                                        {({ input }) => (
                                            <SelectComponent
                                                label={translate('experiencer.commissionForm.currency')}
                                                component={SelectComponent}
                                                values={currencyOptions}
                                                style={{ width: '100%' }}
                                                value={input.value}
                                                onFieldChange={(e) => {
                                                    let value = e.target.value;
                                                    setIsChanged(true);
                                                    input.onChange(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </Grid2>
                                <Grid2 container justifyContent="flex-end">
                                    <Grid2>
                                        <Button
                                            onClick={(e) => {
                                                onClose();
                                                return form.reset(e);
                                            }}>
                                            {translate('btn_cancel')}
                                        </Button>
                                    </Grid2>
                                    <Grid2>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ color: 'white !important', fontWeight: 'bold' }}
                                            disabled={!isChanged}
                                            onClick={(e) => {
                                                return handleSubmit(e);
                                            }}>
                                            {translate('btn_add')}
                                        </Button>
                                    </Grid2>
                                </Grid2>
                            </Grid2>
                        </Grid2>
                    );
                }}
            />
        </Grid2>
    );
};

export default CommissionForm;
